/**
 * Session-probe outcome classification.
 *
 * Validating a session has THREE outcomes, not two, and conflating the last
 * two is what made a network blip look like a sign-out:
 *
 *   - **valid**       — the server returned a user/admin record.
 *   - **no-session**  — the server *answered* and said "you are not signed in"
 *                       (a real 401/403 carrying our JSON envelope, or the
 *                       typed `AUTH_SESSION_INVALID` ghost-session code).
 *   - **unreachable** — we never got an answer: DNS/TLS/CORS failure, timeout,
 *                       aborted request, a 5xx, or an upstream deployment
 *                       protection wall. We know NOTHING about the session.
 *
 * Only `no-session` may cause a redirect to the auth app. Treating
 * `unreachable` as `no-session` bounces a perfectly-signed-in operator to
 * sign-in every time the API is slow, cold, or briefly unreachable — the
 * single largest source of the "it keeps logging me out and back in" report.
 *
 * Pure and dependency-free apart from the deployment-protection code so it
 * stays trivially unit-testable and importable from the Firebase-free clients.
 */

import { DEPLOYMENT_PROTECTED_CODE } from './deploymentProtection';

/** Marker code carried by {@link SessionUnreachableError}. */
export const SESSION_UNREACHABLE_CODE = 'SESSION_UNREACHABLE' as const;

/**
 * Thrown by a session-validation `queryFn` when the probe could not reach a
 * verdict. Guards MUST NOT redirect on this — they should hold the last known
 * state (or render optimistically off the Firebase session) and retry.
 */
export class SessionUnreachableError extends Error {
    readonly code = SESSION_UNREACHABLE_CODE;

    constructor(message: string, options?: { cause?: unknown }) {
        super(message);
        this.name = 'SessionUnreachableError';
        if (options?.cause !== undefined) {
            (this as { cause?: unknown }).cause = options.cause;
        }
    }
}

/** Narrow the loose error shapes that reach us (axios raw, apiClient-wrapped, DOM). */
interface ErrorProbe {
    code?: unknown;
    status?: unknown;
    message?: unknown;
    request?: unknown;
    response?: { status?: unknown } | undefined;
}

const probe = (error: unknown): ErrorProbe => (error ?? {}) as ErrorProbe;

/**
 * HTTP status carried by the error, from either shape:
 *   - `apiClient`-wrapped errors expose it as a top-level `status` field
 *   - raw axios errors expose it at `response.status`
 */
function statusOf(error: unknown): number | undefined {
    const e = probe(error);
    if (typeof e.status === 'number') return e.status;
    const responseStatus = e.response?.status;
    return typeof responseStatus === 'number' ? responseStatus : undefined;
}

function messageOf(error: unknown): string {
    const m = probe(error).message;
    return typeof m === 'string' ? m : '';
}

/**
 * Axios/undici transport failure codes. These mean the request never produced
 * an HTTP response, so there is no verdict to read.
 */
const TRANSPORT_ERROR_CODES = new Set([
    'ERR_NETWORK',
    'ERR_CANCELED',
    'ECONNABORTED',
    'ECONNREFUSED',
    'ECONNRESET',
    'ETIMEDOUT',
    'EAI_AGAIN',
    'ENOTFOUND',
    'UND_ERR_CONNECT_TIMEOUT',
    'UND_ERR_HEADERS_TIMEOUT',
]);

/**
 * True when the probe failed without reaching a verdict about the session.
 *
 * Deliberately includes 5xx: a broken or overloaded API is not a statement
 * about *this user's* credentials, and signing them out for it is wrong.
 */
export function isSessionUnreachable(error: unknown): boolean {
    if (!error) return false;

    const e = probe(error);

    if (e.code === SESSION_UNREACHABLE_CODE) return true;

    // Upstream auth wall (e.g. Vercel Deployment Protection). The request never
    // reached Fastify, so its 401/403 says nothing about our session cookie.
    if (e.code === DEPLOYMENT_PROTECTED_CODE) return true;

    if (typeof e.code === 'string' && TRANSPORT_ERROR_CODES.has(e.code)) return true;

    const status = statusOf(error);
    if (typeof status === 'number' && status >= 500) return true;

    // `fetch` rejects with a bare TypeError on transport failure.
    if (error instanceof TypeError && /fetch|network/i.test(messageOf(error))) return true;

    // Axios error with a request but no response == no answer came back.
    if (e.request && !e.response && status === undefined) return true;

    // Last-resort message sniffing for shapes that lost their code.
    if (/network error|failed to fetch|load failed/i.test(messageOf(error))) return true;

    return false;
}

/**
 * True when the server *definitively* reported no valid session.
 *
 * Requires an actual HTTP verdict — an unreachable probe never qualifies, so
 * the two predicates are mutually exclusive by construction.
 */
export function isDefinitiveNoSession(error: unknown): boolean {
    if (!error) return false;
    if (isSessionUnreachable(error)) return false;

    const e = probe(error);

    // Ghost session: server cleared the cookie and told us so.
    if (e.code === 'AUTH_SESSION_INVALID') return true;

    const status = statusOf(error);
    if (status === 401 || status === 403) return true;

    // Typed codes/messages the API emits alongside a 401/403. Kept as a
    // fallback for shapes that lost their status on the way through.
    const message = messageOf(error);
    return (
        message.includes('AUTH_SESSION_INVALID') ||
        message.includes('Missing session cookie') ||
        message.includes('Not a platform admin') ||
        message.includes('Admin access has been revoked')
    );
}
